import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Users, Search, Linkedin, Instagram, Briefcase, Award, Edit2, Lock, Sparkles, X, Camera, Save, Phone, Building, User, FileText, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Members() {
  const { user, isPending, isAdmin, profile: currentUserProfile, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [membersVisible, setMembersVisible] = useState(true);
  
  // Edit Profile State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const fetchMembers = useCallback(async () => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .in('membership_status', ['active', 'inactive'])
      .order('created_at', { ascending: true });

    if (data) {
      setMembers(data);
      setFilteredMembers(data);
    }
    setLoading(false);
  }, []);

  const fetchSettings = useCallback(async () => {
    const { data } = await supabase
      .from('app_settings')
      .select('*')
      .eq('setting_key', 'members_visible')
      .maybeSingle();

    if (data && data.setting_value) {
      setMembersVisible(data.setting_value.enabled);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
    fetchSettings();
  }, [fetchMembers, fetchSettings]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredMembers(members);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredMembers(members.filter(member =>
        member.full_name?.toLowerCase().includes(query) ||
        member.company?.toLowerCase().includes(query) ||
        member.role?.toLowerCase().includes(query)
      ));
    }
  }, [searchQuery, members]);

  // Open edit profile modal
  const openEditProfile = () => {
    if (currentUserProfile) {
      setEditForm({
        full_name: currentUserProfile.full_name || '',
        phone_number: currentUserProfile.phone_number || '',
        company: currentUserProfile.company || '',
        role: currentUserProfile.role || '',
        work_description: currentUserProfile.work_description || '',
        professional_insights: currentUserProfile.professional_insights || '',
        personal_insights: currentUserProfile.personal_insights || '',
        interests: currentUserProfile.interests || '',
        linkedin_url: currentUserProfile.linkedin_url || '',
        instagram_url: currentUserProfile.instagram_url || '',
        headshot_url: currentUserProfile.headshot_url || '',
      });
      setShowEditModal(true);
    }
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;

    setUploadingImage(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `headshots/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('member-photos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('member-photos')
        .getPublicUrl(filePath);

      setEditForm({ ...editForm, headshot_url: publicUrl });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    }
    setUploadingImage(false);
  };

  // Save profile changes
  const saveProfile = async () => {
    if (!currentUserProfile || !user) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: editForm.full_name,
          phone_number: editForm.phone_number,
          company: editForm.company,
          role: editForm.role,
          work_description: editForm.work_description,
          professional_insights: editForm.professional_insights,
          personal_insights: editForm.personal_insights,
          interests: editForm.interests,
          linkedin_url: editForm.linkedin_url,
          instagram_url: editForm.instagram_url,
          headshot_url: editForm.headshot_url,
        })
        .eq('id', currentUserProfile.id);

      if (error) throw error;

      // Refresh user data and members list
      await refreshUserData();
      await fetchMembers();
      setShowEditModal(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile. Please try again.');
    }
    setSaving(false);
  };

  // Member detail modal
  const MemberModal = ({ member, onClose }) => (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-brand-dark-light rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          <div className="h-32 bg-gradient-to-br from-brand-gold/20 via-yellow-500/10 to-transparent" />
          <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg bg-black/30 hover:bg-black/50 transition-colors">
            <X size={24} className="text-white" />
          </button>
          <img 
            src={member.headshot_url} 
            alt={member.full_name} 
            className="absolute left-1/2 -translate-x-1/2 -bottom-16 w-32 h-32 rounded-2xl object-cover border-4 border-brand-dark-light shadow-xl" 
          />
        </div>
        
        <div className="pt-20 pb-8 px-8 overflow-y-auto max-h-[60vh]">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-white mb-2">{member.full_name}</h1>
            <p className="text-brand-gold">{member.role}</p>
            <p className="text-gray-400">{member.company}</p>
            <div className="flex items-center justify-center gap-3 mt-4">
              <span className={`px-4 py-1.5 rounded-lg text-sm font-bold ${
                member.membership_tier === 'founder' 
                  ? 'bg-gradient-to-r from-brand-gold to-yellow-500 text-black' 
                  : 'bg-brand-gold/10 text-brand-gold border border-brand-gold/30'
              }`}>
                <Award size={14} className="inline mr-1" />
                {member.membership_tier?.toUpperCase()}
              </span>
            </div>
          </div>

          <div className="flex justify-center gap-3 mb-8">
            {member.linkedin_url && (
              <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center hover:bg-blue-500/20 transition-colors">
                <Linkedin size={22} className="text-blue-400" />
              </a>
            )}
            {member.instagram_url && (
              <a href={member.instagram_url} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-pink-500/10 border border-pink-500/30 flex items-center justify-center hover:bg-pink-500/20 transition-colors">
                <Instagram size={22} className="text-pink-400" />
              </a>
            )}
          </div>

          <div className="space-y-6">
            {member.work_description && (
              <div className="glass-card rounded-2xl p-5">
                <h3 className="text-sm font-bold text-brand-gold mb-2 flex items-center gap-2"><Briefcase size={16} /> What They Do</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{member.work_description}</p>
              </div>
            )}
            {member.professional_insights && (
              <div className="glass-card rounded-2xl p-5">
                <h3 className="text-sm font-bold text-brand-gold mb-2">Professional Insights</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{member.professional_insights}</p>
              </div>
            )}
            {member.personal_insights && (
              <div className="glass-card rounded-2xl p-5">
                <h3 className="text-sm font-bold text-brand-gold mb-2">Personal Background</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{member.personal_insights}</p>
              </div>
            )}
            {member.interests && (
              <div className="glass-card rounded-2xl p-5">
                <h3 className="text-sm font-bold text-brand-gold mb-2">Interests</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{member.interests}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Not logged in or pending
  if (!user || isPending) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="glass-card rounded-3xl p-12 text-center">
          <Lock size={48} className="mx-auto text-brand-gold mb-4" />
          <h2 className="text-2xl font-bold gradient-text mb-3">Members Only</h2>
          <p className="text-gray-400 max-w-md mx-auto">
            {!user 
              ? 'Sign in or become a member to view member profiles and connect with our network.' 
              : 'You can view member profiles once your application is approved.'}
          </p>
          {!user && (
            <button 
              onClick={() => navigate('/auth')}
              className="mt-6 btn-primary px-8 py-3 rounded-xl"
            >
              Sign In
            </button>
          )}
        </div>
      </div>
    );
  }

  // Directory hidden
  if (!membersVisible && !isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="glass-card rounded-3xl p-12 text-center">
          <Users size={48} className="mx-auto text-brand-gold mb-4" />
          <h2 className="text-2xl font-bold gradient-text mb-3">Directory Unavailable</h2>
          <p className="text-gray-400">The member directory is currently hidden. Please check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header Section */}
      <div className="glass-card rounded-3xl p-8 mb-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-gold/10 to-transparent" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={20} className="text-brand-gold" />
            <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider">Network</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Member Directory</h1>
          <p className="text-gray-400">{members.length} members strong and growing</p>
        </div>
      </div>

      {/* My Profile Card */}
      {currentUserProfile && (
        <div className="glass-card rounded-2xl p-6 mb-6 border border-brand-gold/30 bg-gradient-to-r from-brand-gold/5 to-transparent" data-testid="my-profile-card">
          <div className="flex items-center">
            <img 
              src={currentUserProfile.headshot_url} 
              alt={currentUserProfile.full_name} 
              className="w-16 h-16 rounded-xl mr-4 object-cover border-2 border-brand-gold/50" 
            />
            <div className="flex-1">
              <p className="font-bold text-white text-lg">{currentUserProfile.full_name}</p>
              <p className="text-brand-gold text-sm">{currentUserProfile.role}</p>
              <p className="text-gray-400 text-sm">{currentUserProfile.company}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-4 py-2 rounded-xl text-xs font-bold ${
                currentUserProfile.membership_tier === 'founder' 
                  ? 'bg-gradient-to-r from-brand-gold to-yellow-500 text-black' 
                  : 'bg-brand-gold/10 text-brand-gold border border-brand-gold/30'
              }`}>
                {currentUserProfile.membership_tier === 'founder' ? 'Founder' : 'Fellow'}
              </span>
              <button 
                onClick={openEditProfile}
                className="w-11 h-11 btn-primary rounded-xl flex items-center justify-center" 
                data-testid="edit-profile-btn"
                title="Edit Profile"
              >
                <Edit2 size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="glass-card rounded-2xl px-5 py-4 mb-6">
        <div className="flex items-center">
          <Search size={20} className="text-brand-gold mr-4" />
          <input
            type="text"
            placeholder="Search by name, company, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent text-white placeholder-gray-500 focus:outline-none text-sm"
            data-testid="search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="text-gray-500 hover:text-white transition-colors">
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Networking Notice */}
      <div className="glass-card rounded-xl p-4 mb-8 border border-brand-gold/10">
        <p className="text-gray-400 text-sm text-center">
          🤝 Online connections are great, but in-person events are where the magic happens. 
          Please refrain from contacting members until you have connected in person at an event.
        </p>
      </div>

      {!membersVisible && isAdmin && (
        <div className="glass-card rounded-xl p-4 mb-6 border border-yellow-500/30 bg-yellow-500/5">
          <p className="text-yellow-400 text-sm text-center">
            ⚠️ The member directory is currently hidden from members. Only you as an admin can view it.
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-gold"></div>
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="glass-card rounded-3xl p-12 text-center">
          <Users size={48} className="mx-auto text-brand-gold mb-4" />
          <p className="text-xl font-bold gradient-text">
            {searchQuery ? 'No members found' : 'No members available'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-testid="members-grid">
          {filteredMembers.map((member, index) => (
            <button 
              key={member.id} 
              onClick={() => setSelectedMember(member)} 
              className="glass-card rounded-2xl p-5 text-center card-hover animate-fadeIn group"
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`member-card-${member.id}`}
            >
              <img 
                src={member.headshot_url} 
                alt={member.full_name} 
                className="w-20 h-20 rounded-xl mx-auto mb-3 object-cover border-2 border-brand-gold/20 group-hover:border-brand-gold/50 transition-colors" 
              />
              <p className="text-sm font-bold text-white truncate mb-1">{member.full_name}</p>
              <p className="text-xs text-brand-gold truncate mb-1">{member.role}</p>
              <p className="text-xs text-gray-500 truncate mb-3">{member.company}</p>
              <span className={`inline-block px-3 py-1 rounded-lg text-xs font-bold ${
                member.membership_tier === 'founder' 
                  ? 'bg-gradient-to-r from-brand-gold to-yellow-500 text-black' 
                  : 'bg-brand-gold/10 text-brand-gold'
              }`}>
                {member.membership_tier === 'founder' ? 'Founder' : 'Fellow'}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Member Detail Modal */}
      {selectedMember && (
        <MemberModal member={selectedMember} onClose={() => setSelectedMember(null)} />
      )}

      {/* Edit Profile Modal - Rendered Inline to prevent re-mounting on state change */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-brand-dark-light rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-brand-gold/20">
              <h2 className="text-xl font-bold gradient-text">Edit Your Profile</h2>
              <button onClick={() => setShowEditModal(false)} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                <X size={24} className="text-gray-400" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[65vh] space-y-6">
              {/* Profile Photo */}
              <div className="flex flex-col items-center">
                <div className="relative">
                  <img 
                    src={editForm.headshot_url || 'https://via.placeholder.com/150'} 
                    alt="Profile" 
                    className="w-28 h-28 rounded-2xl object-cover border-4 border-brand-gold/30" 
                  />
                  <label className="absolute -bottom-2 -right-2 w-10 h-10 bg-brand-gold rounded-xl flex items-center justify-center cursor-pointer hover:bg-yellow-400 transition-colors">
                    <Camera size={18} className="text-black" />
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </label>
                </div>
                {uploadingImage && <p className="text-brand-gold text-sm mt-2">Uploading...</p>}
              </div>

              {/* Basic Info */}
              <div className="glass-card rounded-2xl p-5 space-y-4">
                <h3 className="text-sm font-bold text-brand-gold flex items-center gap-2"><User size={16} /> Basic Information</h3>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Full Name</label>
                  <input 
                    type="text" 
                    value={editForm.full_name || ''} 
                    onChange={e => setEditForm(prev => ({...prev, full_name: e.target.value}))}
                    className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Phone Number</label>
                  <input 
                    type="tel" 
                    value={editForm.phone_number || ''} 
                    onChange={e => setEditForm(prev => ({...prev, phone_number: e.target.value}))}
                    className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors"
                    placeholder="(210) 555-0123"
                  />
                </div>
              </div>

              {/* Professional Info */}
              <div className="glass-card rounded-2xl p-5 space-y-4">
                <h3 className="text-sm font-bold text-brand-gold flex items-center gap-2"><Building size={16} /> Professional Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Company</label>
                    <input 
                      type="text" 
                      value={editForm.company || ''} 
                      onChange={e => setEditForm(prev => ({...prev, company: e.target.value}))}
                      className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors"
                      placeholder="Company name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Role / Title</label>
                    <input 
                      type="text" 
                      value={editForm.role || ''} 
                      onChange={e => setEditForm(prev => ({...prev, role: e.target.value}))}
                      className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors"
                      placeholder="Your role or title"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">What You Do</label>
                  <textarea 
                    value={editForm.work_description || ''} 
                    onChange={e => setEditForm(prev => ({...prev, work_description: e.target.value}))}
                    rows={3}
                    className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors resize-none"
                    placeholder="Describe what you do professionally..."
                  />
                </div>
              </div>

              {/* Insights */}
              <div className="glass-card rounded-2xl p-5 space-y-4">
                <h3 className="text-sm font-bold text-brand-gold flex items-center gap-2"><FileText size={16} /> About You</h3>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Professional Insights</label>
                  <textarea 
                    value={editForm.professional_insights || ''} 
                    onChange={e => setEditForm(prev => ({...prev, professional_insights: e.target.value}))}
                    rows={3}
                    className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors resize-none"
                    placeholder="Share your professional insights, expertise, or advice..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Personal Background</label>
                  <textarea 
                    value={editForm.personal_insights || ''} 
                    onChange={e => setEditForm(prev => ({...prev, personal_insights: e.target.value}))}
                    rows={3}
                    className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors resize-none"
                    placeholder="Share a bit about your personal background..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Interests & Hobbies</label>
                  <textarea 
                    value={editForm.interests || ''} 
                    onChange={e => setEditForm(prev => ({...prev, interests: e.target.value}))}
                    rows={2}
                    className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors resize-none"
                    placeholder="What are your interests outside of work?"
                  />
                </div>
              </div>

              {/* Social Links */}
              <div className="glass-card rounded-2xl p-5 space-y-4">
                <h3 className="text-sm font-bold text-brand-gold flex items-center gap-2"><Linkedin size={16} /> Social Profiles</h3>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">LinkedIn URL</label>
                  <input 
                    type="url" 
                    value={editForm.linkedin_url || ''} 
                    onChange={e => setEditForm(prev => ({...prev, linkedin_url: e.target.value}))}
                    className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors"
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Instagram URL</label>
                  <input 
                    type="url" 
                    value={editForm.instagram_url || ''} 
                    onChange={e => setEditForm(prev => ({...prev, instagram_url: e.target.value}))}
                    className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors"
                    placeholder="https://instagram.com/yourprofile"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-brand-gold/20">
              <button 
                onClick={() => setShowEditModal(false)} 
                className="px-6 py-2.5 rounded-xl bg-white/5 text-gray-400 font-semibold hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={saveProfile} 
                disabled={saving}
                className="btn-primary px-6 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-50"
              >
                <Save size={18} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
